import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  FaGoogle,
  FaApple,
  FaEnvelope,
  FaPhone,
  FaUnlink,
  FaInfoCircle,
} from "react-icons/fa";
import ConfirmModal from "./ConfirmModal";
import {
  isApplePrivateRelay,
  getPrivateRelayDescription,
} from "../utils/privateRelayHelper";

interface LinkedProvider {
  providerId: string;
  email?: string | null;
  displayName?: string | null;
  photoURL?: string | null;
}

export const LinkedAccountsSection: React.FC = () => {
  const {
    currentUser,
    getLinkedProviders,
    linkGoogleAccount,
    linkAppleAccount,
    unlinkProvider,
  } = useAuth();
  const [providers, setProviders] = useState<LinkedProvider[]>([]);
  const [isLinking, setIsLinking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showUnlinkModal, setShowUnlinkModal] = useState<string | null>(null);

  useEffect(() => {
    loadProviders();
  }, [currentUser, getLinkedProviders]);

  const loadProviders = () => {
    const linked = getLinkedProviders();
    setProviders(linked);
  };

  const getProviderIcon = (providerId: string) => {
    switch (providerId) {
      case "google.com":
        return <FaGoogle size={20} color="#DC2626" />;
      case "apple.com":
        return <FaApple size={20} color="#1F2937" />;
      case "password":
        return <FaEnvelope size={20} color="#3B82F6" />;
      case "phone":
        return <FaPhone size={20} color="#10B981" />;
      default:
        return null;
    }
  };

  const getProviderName = (providerId: string) => {
    switch (providerId) {
      case "google.com":
        return "Google";
      case "apple.com":
        return "Apple";
      case "password":
        return "Email/Password";
      case "phone":
        return "Phone";
      default:
        return providerId;
    }
  };

  const handleLinkGoogle = async () => {
    try {
      setIsLinking("google.com");
      setError(null);
      setSuccess(null);

      await linkGoogleAccount();

      setSuccess("Google account linked successfully!");
      loadProviders();
    } catch (err: any) {
      setError(err.message || "Failed to link Google account");
    } finally {
      setIsLinking(null);
    }
  };

  const handleLinkApple = async () => {
    try {
      setIsLinking("apple.com");
      setError(null);
      setSuccess(null);

      await linkAppleAccount();

      setSuccess("Apple account linked successfully!");
      loadProviders();
    } catch (err: any) {
      setError(err.message || "Failed to link Apple account");
    } finally {
      setIsLinking(null);
    }
  };

  const handleUnlink = async (providerId: string) => {
    try {
      setError(null);
      setSuccess(null);

      await unlinkProvider(providerId);

      setSuccess(
        `${getProviderName(providerId)} account unlinked successfully!`,
      );
      loadProviders();
      setShowUnlinkModal(null);
    } catch (err: any) {
      setError(err.message || "Failed to unlink account");
      setShowUnlinkModal(null);
    }
  };

  const isProviderLinked = (providerId: string) => {
    return providers.some((p) => p.providerId === providerId);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
        Linked Accounts
      </h2>

      <p className="text-gray-600 dark:text-gray-400 mb-6">
        Link multiple sign-in methods to your account for easier access.
      </p>

      {error && (
        <div className="mb-4 p-4 bg-red-100 dark:bg-red-900/20 border border-red-400 dark:border-red-800 rounded-lg">
          <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-100 dark:bg-green-900/20 border border-green-400 dark:border-green-800 rounded-lg">
          <p className="text-green-700 dark:text-green-400 text-sm">
            {success}
          </p>
        </div>
      )}

      <div className="space-y-4">
        {/* Currently Linked Accounts */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
            Connected
          </h3>

          {providers.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-sm italic">
              No accounts linked yet
            </p>
          ) : (
            providers.map((provider) => {
              const isPrivateRelay = isApplePrivateRelay(provider.email);
              return (
                <div
                  key={provider.providerId}
                  className={`flex items-center justify-between p-4 rounded-lg ${
                    isPrivateRelay
                      ? "bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800"
                      : "bg-gray-50 dark:bg-gray-700"
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="text-2xl">
                      {getProviderIcon(provider.providerId)}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        {getProviderName(provider.providerId)}
                        {isPrivateRelay && (
                          <span className="inline-flex items-center gap-1 text-xs bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-200 px-2 py-0.5 rounded-full">
                            <FaInfoCircle /> Hidden Email
                          </span>
                        )}
                      </p>
                      {provider.email && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {isPrivateRelay
                            ? getPrivateRelayDescription(provider.email)
                            : provider.email}
                        </p>
                      )}
                      {isPrivateRelay && providers.length === 1 && (
                        <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
                          💡 Link Google to access from multiple sign-in methods
                        </p>
                      )}
                    </div>
                  </div>

                  {providers.length > 1 && (
                    <button
                      onClick={() => setShowUnlinkModal(provider.providerId)}
                      className="flex items-center space-x-2 px-4 py-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                    >
                      <FaUnlink />
                      <span className="text-sm">Unlink</span>
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Available to Link */}
        <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
            Available to Link
          </h3>

          <div className="space-y-2">
            {!isProviderLinked("google.com") && (
              <button
                onClick={handleLinkGoogle}
                disabled={isLinking === "google.com"}
                className="w-full flex items-center justify-between p-4 bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-lg hover:border-red-500 dark:hover:border-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex items-center space-x-3">
                  <FaGoogle size={24} color="#DC2626" />
                  <span className="font-semibold text-gray-900 dark:text-white">
                    Link Google Account
                  </span>
                </div>
                {isLinking === "google.com" && (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-500" />
                )}
              </button>
            )}

            {!isProviderLinked("apple.com") && (
              <button
                onClick={handleLinkApple}
                disabled={isLinking === "apple.com"}
                className="w-full flex items-center justify-between p-4 bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-lg hover:border-gray-800 dark:hover:border-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex items-center space-x-3">
                  <FaApple size={24} color="#1F2937" />
                  <span className="font-semibold text-gray-900 dark:text-white">
                    Link Apple Account
                  </span>
                </div>
                {isLinking === "apple.com" && (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-800 dark:border-white" />
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Unlink Confirmation Modal */}
      {showUnlinkModal && (
        <ConfirmModal
          isOpen={true}
          onCancel={() => setShowUnlinkModal(null)}
          onConfirm={() => handleUnlink(showUnlinkModal)}
          title="Unlink Account"
          message={`Are you sure you want to unlink your ${getProviderName(showUnlinkModal)} account? You can link it again later.`}
          confirmText="Unlink"
          confirmStyle="danger"
        />
      )}
    </div>
  );
};
